/**
 * Workflow Progress Store
 * =======================
 * Tracks user's progress through the guided flow.
 * Persists completion state and determines next logical step.
 * 
 * Flow: Income Path → Content Ideas → Weekly Plan → Videos → Today's Post Ready
 */
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import api from '@/utils/api';

// Flow steps in order
export const FLOW_STEPS = {
  INCOME_PATH: 'income_path',
  CONTENT_IDEAS: 'content_ideas',
  WEEKLY_PLAN: 'weekly_plan',
  CREATE_VIDEOS: 'create_videos',
  TODAYS_POST: 'todays_post'
};

// Step order for progression
const STEP_ORDER = [
  FLOW_STEPS.INCOME_PATH,
  FLOW_STEPS.CONTENT_IDEAS,
  FLOW_STEPS.WEEKLY_PLAN,
  FLOW_STEPS.CREATE_VIDEOS,
  FLOW_STEPS.TODAYS_POST
];

const initialProgress = {
  // Completion flags
  incomePathCompleted: false,
  contentIdeasCompleted: false,
  weeklyPlanCompleted: false,
  videosCreated: false,
  todaysPostReady: false,
  
  // Timestamps
  incomePathCompletedAt: null,
  contentIdeasCompletedAt: null,
  weeklyPlanCompletedAt: null,
  videosCreatedAt: null,
  todaysPostReadyAt: null,
  
  // IDs for linking content
  incomePathId: null,
  weeklyPlanId: null,
  todaysContentId: null,
  todaysVideoId: null,
  
  // Current position
  lastCompletedStep: null,
  currentStep: FLOW_STEPS.INCOME_PATH,
  
  // Today's specific data
  todaysContentType: null,
  todaysTopicFromPlan: null,
  
  // Sync state
  lastSyncedAt: null,
  isLoading: false
};

export const useWorkflowProgress = create(
  persist(
    (set, get) => ({
      ...initialProgress,
      
      // =========================================
      // Step Completion Methods
      // =========================================
      
      completeIncomePath: (data = {}) => {
        const now = new Date().toISOString();
        set({
          incomePathCompleted: true,
          incomePathCompletedAt: now,
          incomePathId: data.id || null,
          lastCompletedStep: FLOW_STEPS.INCOME_PATH,
          currentStep: FLOW_STEPS.CONTENT_IDEAS
        });
        get().syncToBackend();
      },
      
      completeContentIdeas: (data = {}) => {
        const now = new Date().toISOString();
        set({
          contentIdeasCompleted: true,
          contentIdeasCompletedAt: now,
          lastCompletedStep: FLOW_STEPS.CONTENT_IDEAS,
          currentStep: FLOW_STEPS.WEEKLY_PLAN
        });
        get().syncToBackend();
      },
      
      completeWeeklyPlan: (data = {}) => {
        const now = new Date().toISOString();
        set({
          weeklyPlanCompleted: true,
          weeklyPlanCompletedAt: now,
          weeklyPlanId: data.id || null,
          lastCompletedStep: FLOW_STEPS.WEEKLY_PLAN,
          currentStep: FLOW_STEPS.CREATE_VIDEOS,
          todaysTopicFromPlan: data.todaysTopic || null
        });
        get().syncToBackend();
      },
      
      completeVideoCreation: (data = {}) => {
        const now = new Date().toISOString();
        set({
          videosCreated: true,
          videosCreatedAt: now,
          todaysVideoId: data.videoId || null,
          todaysContentId: data.contentId || null,
          lastCompletedStep: FLOW_STEPS.CREATE_VIDEOS,
          currentStep: FLOW_STEPS.TODAYS_POST
        });
        get().syncToBackend();
      },
      
      markTodaysPostReady: (data = {}) => {
        const now = new Date().toISOString();
        set({
          todaysPostReady: true,
          todaysPostReadyAt: now,
          todaysContentId: data.contentId || get().todaysContentId,
          todaysVideoId: data.videoId || get().todaysVideoId,
          todaysContentType: data.contentType || null,
          lastCompletedStep: FLOW_STEPS.TODAYS_POST,
          currentStep: FLOW_STEPS.TODAYS_POST
        });
        get().syncToBackend();
      },
      
      // =========================================
      // Navigation Helpers
      // =========================================
      
      // Get the recommended next step
      getNextStep: () => {
        const state = get();
        
        if (!state.incomePathCompleted) return FLOW_STEPS.INCOME_PATH;
        if (!state.contentIdeasCompleted) return FLOW_STEPS.CONTENT_IDEAS;
        if (!state.weeklyPlanCompleted) return FLOW_STEPS.WEEKLY_PLAN;
        if (!state.videosCreated) return FLOW_STEPS.CREATE_VIDEOS;
        return FLOW_STEPS.TODAYS_POST;
      },
      
      // Get human-readable next action
      getNextAction: () => {
        const nextStep = get().getNextStep();
        const actions = {
          [FLOW_STEPS.INCOME_PATH]: { label: 'Create Income Path', action: 'open_monetization' },
          [FLOW_STEPS.CONTENT_IDEAS]: { label: 'Get Content Ideas', action: 'open_viral_ideas' },
          [FLOW_STEPS.WEEKLY_PLAN]: { label: 'Plan My Week', action: 'open_run_my_week' },
          [FLOW_STEPS.CREATE_VIDEOS]: { label: 'Create My Videos', action: 'open_generate_content' },
          [FLOW_STEPS.TODAYS_POST]: { label: 'View Today\'s Post', action: 'scroll_to_today' }
        };
        return actions[nextStep] || actions[FLOW_STEPS.INCOME_PATH];
      },
      
      // Check if a step is unlocked (previous steps completed)
      isStepUnlocked: (step) => {
        const state = get();
        const stepIndex = STEP_ORDER.indexOf(step);
        
        // First step is always unlocked
        if (stepIndex === 0) return true;
        
        // Check all previous steps are completed
        for (let i = 0; i < stepIndex; i++) {
          const prevStep = STEP_ORDER[i];
          if (prevStep === FLOW_STEPS.INCOME_PATH && !state.incomePathCompleted) return false;
          if (prevStep === FLOW_STEPS.CONTENT_IDEAS && !state.contentIdeasCompleted) return false;
          if (prevStep === FLOW_STEPS.WEEKLY_PLAN && !state.weeklyPlanCompleted) return false;
          if (prevStep === FLOW_STEPS.CREATE_VIDEOS && !state.videosCreated) return false;
        }
        return true;
      },
      
      // Get completion percentage
      getProgressPercentage: () => {
        const state = get();
        let completed = 0;
        if (state.incomePathCompleted) completed++;
        if (state.contentIdeasCompleted) completed++;
        if (state.weeklyPlanCompleted) completed++;
        if (state.videosCreated) completed++;
        if (state.todaysPostReady) completed++;
        return Math.round((completed / 5) * 100);
      },
      
      // Get status summary
      getStatusSummary: () => {
        const state = get();
        return {
          incomePathCompleted: state.incomePathCompleted,
          contentIdeasCompleted: state.contentIdeasCompleted,
          weeklyPlanCompleted: state.weeklyPlanCompleted,
          videosCreated: state.videosCreated,
          todaysPostReady: state.todaysPostReady,
          progressPercentage: get().getProgressPercentage(),
          nextStep: get().getNextStep(),
          nextAction: get().getNextAction()
        };
      },
      
      // =========================================
      // Backend Sync
      // =========================================
      
      syncToBackend: async () => {
        const state = get();
        try {
          await api.post('/auth/wizard/progress', {
            income_path_completed: state.incomePathCompleted,
            content_ideas_completed: state.contentIdeasCompleted,
            weekly_plan_completed: state.weeklyPlanCompleted,
            videos_created: state.videosCreated,
            todays_post_ready: state.todaysPostReady,
            last_completed_step: state.lastCompletedStep,
            current_step: state.currentStep
          }, { params: { step: 99 } }); // Use step 99 for progress sync
          
          set({ lastSyncedAt: new Date().toISOString() });
        } catch (error) {
          console.error('Failed to sync workflow progress:', error);
        }
      },
      
      syncFromBackend: async () => {
        set({ isLoading: true });
        try {
          const response = await api.get('/auth/wizard/status');
          const wizardData = response.data?.wizard_data || {};
          
          if (wizardData.income_path_completed !== undefined) {
            set({
              incomePathCompleted: wizardData.income_path_completed || false,
              contentIdeasCompleted: wizardData.content_ideas_completed || false,
              weeklyPlanCompleted: wizardData.weekly_plan_completed || false,
              videosCreated: wizardData.videos_created || false,
              todaysPostReady: wizardData.todays_post_ready || false,
              lastCompletedStep: wizardData.last_completed_step || null,
              currentStep: wizardData.current_step || FLOW_STEPS.INCOME_PATH,
              lastSyncedAt: new Date().toISOString(),
              isLoading: false
            });
          } else {
            set({ isLoading: false });
          }
        } catch (error) {
          console.error('Failed to sync from backend:', error);
          set({ isLoading: false });
        }
      },
      
      // =========================================
      // Reset Methods
      // =========================================
      
      // Reset today's progress only (for new day)
      resetTodaysProgress: () => set({
        todaysPostReady: false,
        todaysPostReadyAt: null,
        todaysContentId: null,
        todaysVideoId: null,
        todaysContentType: null,
        currentStep: get().videosCreated ? FLOW_STEPS.CREATE_VIDEOS : get().currentStep
      }),
      
      // Full reset (start fresh)
      resetAllProgress: () => set(initialProgress)
    }),
    {
      name: 'paygbot-workflow-progress',
      version: 1
    }
  )
);

export default useWorkflowProgress;
